import type { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community'
import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'

export interface DateWindowFilterParams extends IFilterParams {
  /** Field name for the window start date (defaults to field + '_start' or 'delivery_window_start') */
  startField?: string
  /** Field name for the window end date (defaults to field + '_end' or 'delivery_window_end') */
  endField?: string
}

export interface DateWindowFilterModel {
  filterType: 'dateWindow'
  dateValue: string | null
}

/**
 * Custom AG Grid filter for date window/range columns.
 * Filters rows where the selected date falls within the window (start <= date <= end).
 *
 * Usage in column definition:
 * ```
 * {
 *   headerName: 'DELIVERY WINDOW',
 *   field: 'delivery_window_start',
 *   filter: DateWindowFilter,
 *   filterParams: {
 *     startField: 'delivery_window_start',
 *     endField: 'delivery_window_end',
 *   },
 * }
 * ```
 */
export const DateWindowFilter = forwardRef(function DateWindowFilter(
  props: DateWindowFilterParams,
  ref,
) {
  // Use ref for filter value to avoid stale closure issues
  const dateRef = useRef<Dayjs | null>(null)
  const [, forceUpdate] = useState(0)

  // Determine field names for start and end dates
  const startField =
    props.startField || props.colDef.field || 'delivery_window_start'
  const endField =
    props.endField ||
    startField.replace('_start', '_end') ||
    'delivery_window_end'

  // Expose AG Grid filter interface
  useImperativeHandle(ref, () => ({
    doesFilterPass(params: IDoesFilterPassParams) {
      const selectedDate = dateRef.current
      if (!selectedDate) return true

      const data = params.data
      const startValue = data?.[startField]
      const endValue = data?.[endField]

      if (!startValue || !endValue) return false

      const windowStart = dayjs(startValue).startOf('day').valueOf()
      const windowEnd = dayjs(endValue).startOf('day').valueOf()
      const filterDate = selectedDate.startOf('day').valueOf()

      return filterDate >= windowStart && filterDate <= windowEnd
    },

    isFilterActive() {
      return dateRef.current !== null
    },

    getModel(): DateWindowFilterModel | null {
      if (!dateRef.current) return null
      return {
        filterType: 'dateWindow',
        dateValue: dateRef.current.toISOString(),
      }
    },

    setModel(model: DateWindowFilterModel | null) {
      if (model?.dateValue) {
        dateRef.current = dayjs(model.dateValue)
      } else {
        dateRef.current = null
      }
      forceUpdate((n) => n + 1)
    },
  }))

  const handleDateChange = (date: Dayjs | null) => {
    dateRef.current = date
    forceUpdate((n) => n + 1)
    props.filterChangedCallback()
  }

  return (
    <div style={{ padding: 12, minWidth: 200 }}>
      <div
        style={{
          marginBottom: 8,
          fontSize: 12,
          color: 'var(--text-secondary)',
        }}
      >
        Show orders deliverable on:
      </div>
      <DatePicker
        value={dateRef.current}
        onChange={handleDateChange}
        style={{ width: '100%' }}
        placeholder='Select date'
        allowClear
      />
    </div>
  )
})
